// Lifestyle Value Display Utility
// Transforms raw lifestyle parameter values into user-friendly, human-readable labels

import { getAllLifestyleParameters } from './lifestyleDisplayMapping';

/**
 * Transforms a raw lifestyle parameter value into a user-friendly display format
 * @param {string} parameterKey - The standardized parameter key (e.g., 'lifestyle_nutrition_protein_intake')
 * @param {any} rawValue - The raw value from the database
 * @param {Object} options - Display options
 * @param {boolean} options.includeRawValue - Whether to include the raw value in parentheses (default: true)
 * @param {boolean} options.shortFormat - Whether to use a shorter format for mobile (default: false)
 * @returns {string} - Formatted display value
 */
export const formatLifestyleValueForDisplay = (parameterKey, rawValue, options = {}) => {
  const { includeRawValue = true, shortFormat = false } = options;

  // Handle null, undefined, or empty values
  if (rawValue === null || rawValue === undefined || rawValue === '') {
    return 'N/A';
  }

  // Find the parameter definition
  let allParams;
  try {
    allParams = getAllLifestyleParameters();
  } catch (error) {
    console.error('[formatLifestyleValueForDisplay] Error getting allParams:', error);
    return String(rawValue);
  }
  // Find the parameter in the allParams list
  let param = null;

  param = allParams.find(p => {
      // Handle different parameter key formats:
      // 1. 'Lifestyle_protein_intake' (flowsheet format)
      // 2. 'lifestyle_nutrition_protein_intake' (standardized format)
      // 3. 'protein_intake' (raw parameter key)

      let normalizedKey = parameterKey;

      // Remove 'Lifestyle_' prefix if present and convert to standardized format
      if (normalizedKey.startsWith('Lifestyle_')) {
        normalizedKey = `lifestyle_${normalizedKey.substring(10)}`;
      }

      // Direct match with standardized key
      if (p.key === normalizedKey) {
        return true;
      }

      // If it's a standardized format (lifestyle_category_param), also try matching the param part
      if (normalizedKey.startsWith('lifestyle_')) {
        const parts = normalizedKey.split('_');
        if (parts.length >= 3) {
          // Extract parameter key from lifestyle_category_param format
          const paramPart = parts.slice(2).join('_');
          // Check if p.key ends with this param part
          if (p.key.endsWith(`_${paramPart}`)) {
            return true;
          }
        }
      }

      return false;
    });

  if (!param) {
    // Enhanced fallback for unknown parameters
    console.warn(`[lifestyleValueDisplay] Parameter not found: ${parameterKey}`, { rawValue, allParamsCount: allParams.length });

    // Special handling for known lifestyle parameters that might not be in the mapping
    if (parameterKey.includes('lifestyle_recovery_bedtime_consistency')) {
      param = {
        key: parameterKey,
        name: 'Bedtime Consistency',
        type: 'Questionnaire'
      };
    } else if (rawValue === true || rawValue === false) {
      // If it looks like a boolean, format it as such
      return formatBooleanValue(rawValue, includeRawValue);
    } else {
      return String(rawValue);
    }
  }

  // Handle different parameter types
  switch (param.type) {
    case 'Questionnaire':
      return formatQuestionnaireValue(param, rawValue, includeRawValue, shortFormat);

    case 'BooleanCheckbox':
    case 'BooleanCheckboxOption':
      return formatBooleanValue(rawValue, includeRawValue);

    default:
      // Enhanced fallback for unknown types
      if (rawValue === true || rawValue === false) {
        return formatBooleanValue(rawValue, includeRawValue);
      }
      return formatGenericValue(rawValue, param.units, includeRawValue);
  }
};

/**
 * Formats questionnaire-type values with their option labels
 */
const formatQuestionnaireValue = (param, rawValue, includeRawValue, shortFormat) => {
  if (!param.options || !Array.isArray(param.options)) {
    return String(rawValue);
  }

  // Try to find matching option with flexible type matching
  let selectedOption = param.options.find(option => option.value === rawValue);

  // If no exact match, try with type conversion
  if (!selectedOption) {
    selectedOption = param.options.find(option => {
      // Try string to number conversion
      if (typeof rawValue === 'string' && !isNaN(rawValue)) {
        return option.value === Number(rawValue);
      }
      // Try number to string conversion
      if (typeof rawValue === 'number') {
        return option.value === String(rawValue);
      }
      return false;
    });
  }

  if (!selectedOption) {
    // Special handling for common lifestyle values
    if (rawValue === 'always') return 'Daily (7 days/week)';
    if (rawValue === 'often') return 'Often (5-6 days/week)';
    if (rawValue === 'sometimes') return 'Sometimes (3-4 days/week)';
    if (rawValue === 'rarely') return 'Rarely (1-2 days/week)';
    if (rawValue === 'never') return 'Never (0 days/week)';

    // For numeric values with common patterns
    if (rawValue === '0') return '0';
    if (rawValue === '1') return '1';
    if (rawValue === '2') return '2';
    if (rawValue === '3') return '3';
    if (rawValue === '4+') return '4 or more';

    return String(rawValue);
  }

  let displayLabel = selectedOption.label;

  // For mobile/short format, try to abbreviate common patterns
  if (shortFormat) {
    displayLabel = abbreviateLabel(displayLabel);
  }

  // Extract just the descriptive part without the raw value in parentheses
  // For example: "Never (0 days)" -> "Never"
  if (!includeRawValue && displayLabel.includes('(')) {
    const mainPart = displayLabel.split('(')[0].trim();
    if (mainPart) {
      displayLabel = mainPart;
    }
  }

  // Include raw value in parentheses only if explicitly requested and it adds meaningful information
  if (includeRawValue && !displayLabel.includes(`(${rawValue})`)) {
    // Check if the label already contains the raw value in some form
    const labelContainsValue = displayLabel.toLowerCase().includes(String(rawValue).toLowerCase());
    // Only add raw value if it's not already represented in the label and it's not a simple numeric value
    if (!labelContainsValue && !(/^\d+(\+)?$/.test(String(rawValue)))) {
      displayLabel = `${displayLabel} (${rawValue})`;
    }
  }

  return displayLabel;
};

/**
 * Formats boolean checkbox values
 */
const formatBooleanValue = (rawValue, includeRawValue) => {
  const displayValue = rawValue === true ? 'Yes' : rawValue === false ? 'No' : 'N/A';

  // Only include raw value if explicitly requested and it adds value
  if (includeRawValue && rawValue !== null && rawValue !== undefined && rawValue !== true && rawValue !== false) {
    return `${displayValue} (${rawValue})`;
  }

  return displayValue;
};

/**
 * Formats generic numeric or text values
 */
const formatGenericValue = (rawValue, units, includeRawValue) => {
  let displayValue = String(rawValue);
  
  // Format numeric values
  if (typeof rawValue === 'number') {
    displayValue = Number.isInteger(rawValue) ? String(rawValue) : rawValue.toFixed(1);
  }
  
  // Add units if available
  if (units) {
    displayValue = `${displayValue} ${units}`;
  }
  
  return displayValue;
};

/**
 * Abbreviates common label patterns for mobile display
 */
const abbreviateLabel = (label) => {
  // Common abbreviation patterns
  const abbreviations = {
    'Never (0 days)': 'Never',
    'Rarely (1-2 days)': 'Rarely',
    'Sometimes (3-4 days)': 'Sometimes',
    'Often (5-6 days)': 'Often',
    'Always (7 days)': 'Always',
    'Less than': '<',
    'More than': '>',
    'or more': '+',
    'minutes': 'min',
    'hours': 'hrs'
  };
  
  let abbreviated = label;
  Object.entries(abbreviations).forEach(([full, short]) => {
    abbreviated = abbreviated.replace(full, short);
  });
  
  return abbreviated;
};

/**
 * Batch formats multiple lifestyle values for display
 * @param {Object} valueMap - Object with parameterKey: rawValue pairs
 * @param {Object} options - Display options
 * @returns {Object} - Object with parameterKey: formattedValue pairs
 */
export const formatMultipleLifestyleValues = (valueMap, options = {}) => {
  if (!valueMap || typeof valueMap !== 'object') {
    return {};
  }
  
  const formattedValues = {};
  
  Object.entries(valueMap).forEach(([parameterKey, rawValue]) => {
    formattedValues[parameterKey] = formatLifestyleValueForDisplay(parameterKey, rawValue, options);
  });
  
  return formattedValues;
};

/**
 * Gets the appropriate display format based on screen size
 * @param {boolean} isMobile - Whether the display is on mobile
 * @returns {Object} - Display options object
 */
export const getDisplayOptionsForScreenSize = (isMobile = false) => {
  return {
    includeRawValue: false, // Changed to false for cleaner display
    shortFormat: isMobile
  };
};

/**
 * Validates if a parameter key is a lifestyle parameter
 * @param {string} parameterKey - The parameter key to validate
 * @returns {boolean} - True if it's a lifestyle parameter
 */
export const isLifestyleParameter = (parameterKey) => {
  if (!parameterKey || typeof parameterKey !== 'string') {
    return false;
  }

  // Check for different formats:
  // 1. 'Lifestyle_protein_intake' (flowsheet format)
  // 2. 'lifestyle_nutrition_protein_intake' (standardized format)
  if (parameterKey.startsWith('Lifestyle_') || parameterKey.startsWith('lifestyle_')) {
    return true;
  }

  // Check if it's a raw parameter key that exists in lifestyle categories
  const allParams = getAllLifestyleParameters();
  return allParams.some(p => p.key === parameterKey);
};

/**
 * Gets the category for a lifestyle parameter for styling purposes
 * @param {string} parameterKey - The parameter key
 * @returns {string} - The category name (nutrition, movement, recovery, emotion, environment)
 */
export const getLifestyleParameterCategory = (parameterKey) => {
  if (!isLifestyleParameter(parameterKey)) {
    return null;
  }

  let normalizedKey = parameterKey;

  // Remove 'Lifestyle_' prefix if present
  if (normalizedKey.startsWith('Lifestyle_')) {
    normalizedKey = normalizedKey.substring(10);
  }

  // Extract category from standardized key format: lifestyle_<category>_<parameter>
  if (normalizedKey.startsWith('lifestyle_')) {
    const parts = normalizedKey.split('_');
    if (parts.length >= 2) {
      return parts[1]; // Return the category part
    }
  }

  // If it's a raw parameter key, find it in the categories
  const { lifestyleCategories } = require('./lifestyleCategories');
  for (const category of lifestyleCategories) {
    if (category.parameters.some(p => p.key === normalizedKey)) {
      return category.key;
    }
  }

  return null;
};

const lifestyleValueDisplayUtils = {
  formatLifestyleValueForDisplay,
  formatMultipleLifestyleValues,
  getDisplayOptionsForScreenSize,
  isLifestyleParameter,
  getLifestyleParameterCategory
};

export default lifestyleValueDisplayUtils;
